import { apiFetch } from '../config/api';

const STORAGE_KEY = 'matica_designs';

function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Helper to sanitize dates
function sanitizeDate(dateStr: any): string {
    if (!dateStr) return new Date().toISOString();
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

export interface Design {
    id: string;
    accountId?: string; // Vinculación con el ID de cuenta del cliente
    name: string;
    thumbnail: string;
    canvasData: any;
    databaseConfig?: any;
    createdAt: string;
    updatedAt: string;
    importedAt?: string;
    // Campos de compatibilidad
    front_canvas_json?: string;
    back_canvas_json?: string;
    is_double_sided?: boolean;
}

export const DesignStorageService = {
    // Obtener todos los diseños, fusionando API y Local
    getAllDesigns: async (accountId?: string): Promise<Design[]> => {
        let apiDesigns: Design[] = [];
        let localDesigns: Design[] = [];

        // 1. Fetch Local
        try {
            const designsStr = localStorage.getItem(STORAGE_KEY);
            localDesigns = designsStr ? JSON.parse(designsStr) : [];
            // Validate and fix designs
            localDesigns = localDesigns.map(d => ({
                ...d,
                name: d.name || 'Sin Nombre',
                thumbnail: d.thumbnail || '',
                createdAt: sanitizeDate(d.createdAt),
                updatedAt: sanitizeDate(d.updatedAt),
                canvasData: d.canvasData || {}
            }));
            if (accountId) {
                localDesigns = localDesigns.filter(d => d.accountId === accountId);
            }
        } catch (e) {
            console.warn('Error reading local designs:', e);
        }

        // 2. Fetch API
        try {
            const response = await apiFetch(`/api/designs${accountId ? `?accountId=${accountId}` : ''}`);
            if (Array.isArray(response)) {
                apiDesigns = response;
            } else if (response && response.designs && Array.isArray(response.designs)) {
                apiDesigns = response.designs;
            }

            // SEGURIDAD ADICIONAL: Filtrar lo que viene de la API por si acaso
            if (accountId) {
                apiDesigns = apiDesigns.filter(d => d.accountId === accountId);
            }
        } catch (error) {
            console.warn('API unavailable for getAllDesigns, using local only.', error);
        }

        // 3. Merge (Prefer newer)
        const mergedMap = new Map<string, Design>();

        // Add API first
        apiDesigns.forEach(d => {
            d.createdAt = sanitizeDate(d.createdAt);
            d.updatedAt = sanitizeDate(d.updatedAt);
            mergedMap.set(d.id, d);
        });

        // Merge Local (overwrite if newer or new, or if API is empty)
        localDesigns.forEach(localD => {
            const apiD = mergedMap.get(localD.id);
            if (!apiD) {
                mergedMap.set(localD.id, localD);
            } else {
                // Compare dates
                const localDate = new Date(localD.updatedAt).getTime();
                const apiDate = new Date(apiD.updatedAt).getTime();

                // Check content validity
                const apiHasData = apiD.canvasData && (apiD.canvasData.front || apiD.canvasData.back);
                const localHasData = localD.canvasData && (localD.canvasData.front || localD.canvasData.back);

                // Prefer local if:
                // 1. It's newer OR EQUAL (critical for immediate save/load consistency)
                // 2. API is newer but seems empty/broken while local has data
                if (localDate >= apiDate) {
                    mergedMap.set(localD.id, localD);
                } else if (!apiHasData && localHasData) {
                    console.warn(`Ignoring newer API design ${localD.id} because it lacks data compared to local.`);
                    mergedMap.set(localD.id, localD);
                }
            }
        });

        // Get values
        let finalDesigns = Array.from(mergedMap.values());

        // SEGURIDAD FINAL: Asegurar que absolutamente nada que no sea del usuario se devuelva
        if (accountId) {
            finalDesigns = finalDesigns.filter(d => d.accountId === accountId);
        }

        // Sort by updatedAt desc
        return finalDesigns.sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
    },

    // Obtener un diseño por ID (Smart Fetch)
    getDesignById: async (id: string): Promise<Design | undefined> => {
        let apiDesign: Design | undefined;
        let localDesign: Design | undefined;

        // Obtener accountId del usuario actual
        const accountId = localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID';

        // 1. Try Local
        try {
            const designsStr = localStorage.getItem(STORAGE_KEY);
            const designs: Design[] = designsStr ? JSON.parse(designsStr) : [];
            localDesign = designs.find(d => d.id === id);
        } catch (e) { console.warn(e); }

        // 2. Try API (CON accountId para validación de seguridad)
        try {
            const response = await apiFetch(`/api/designs/${id}?accountId=${accountId}`);
            if (response && (response.id || response.design)) {
                apiDesign = response.design || response;
            }
        } catch (error) {
            console.warn(`API fetch failed for design ${id}`, error);
        }

        // 3. Decide
        if (apiDesign && localDesign) {
            const apiDate = new Date(apiDesign.updatedAt || 0).getTime();
            const localDate = new Date(localDesign.updatedAt || 0).getTime();

            const apiHasData = apiDesign.canvasData && (apiDesign.canvasData.front || apiDesign.canvasData.back);
            const localHasData = localDesign.canvasData && (localDesign.canvasData.front || localDesign.canvasData.back);

            if (localDate >= apiDate) return localDesign;
            if (!apiHasData && localHasData) return localDesign;

            return apiDesign;
        }

        return apiDesign || localDesign;
    },

    // Guardar un nuevo diseño o actualizar existente
    saveDesign: async (designData: Partial<Design>): Promise<Design> => {
        const timestamp = new Date().toISOString();
        let finalDesign: Design;

        // IMPORTANTE: Obtener el accountId del usuario autenticado actual
        const currentAccountId = localStorage.getItem('accountId');
        if (!currentAccountId) {
            throw new Error('No hay usuario autenticado. Por favor, inicie sesión.');
        }

        // 1. Preparar el objeto base del diseño
        const tempId = designData.id || uuidv4();

        finalDesign = {
            id: tempId,
            accountId: currentAccountId,  // SIEMPRE usar el accountId del usuario actual
            name: designData.name || 'Sin Nombre',
            thumbnail: designData.thumbnail || '',
            canvasData: designData.canvasData || {},
            databaseConfig: designData.databaseConfig,
            createdAt: designData.createdAt || timestamp,
            updatedAt: timestamp,
            front_canvas_json: designData.front_canvas_json,
            back_canvas_json: designData.back_canvas_json,
            is_double_sided: designData.is_double_sided
        } as Design;

        // 2. Intentar guardar en API
        try {
            const method = designData.id ? 'PUT' : 'POST';
            const url = designData.id ? `/api/designs/${designData.id}` : '/api/designs';

            // IMPORTANTE: Enviar finalDesign que tiene el accountId correcto
            const response = await apiFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(finalDesign)  // Enviar finalDesign, no designData
            });

            // Si la API responde con un diseño válido o un ID, actualizamos SOLO metadatos críticos
            // NO sobrescribimos canvasData con lo que devuelva el servidor, porque si el servidor es antiguo
            // podría devolver el objeto sin los datos del canvas, borrando nuestro trabajo local.
            if (response) {
                const serverDesign = response.design || response;
                if (serverDesign && serverDesign.id) {
                    finalDesign.id = serverDesign.id;
                    if (serverDesign.createdAt) finalDesign.createdAt = serverDesign.createdAt;
                    if (serverDesign.updatedAt) finalDesign.updatedAt = serverDesign.updatedAt;
                    // NO hacemos { ...finalDesign, ...serverDesign } para proteger los datos locales
                }
            }
        } catch (error) {
            console.warn('Backend API save failed or incompatible. Using local storage only.', error);
            // No lanzamos error, continuamos con el guardado local
        }

        // 3. Guardar en LocalStorage (Siempre, como fuente de verdad local)
        try {
            const designsStr = localStorage.getItem(STORAGE_KEY);
            const designs: Design[] = designsStr ? JSON.parse(designsStr) : [];

            const index = designs.findIndex(d => d.id === finalDesign.id);
            if (index !== -1) {
                designs[index] = finalDesign;
            } else {
                designs.push(finalDesign);
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(designs));
        } catch (e: any) {
            console.error('LocalStorage error:', e);
            if (e.name === 'QuotaExceededError') {
                // Intentar limpiar espacio si está lleno
                const designsStr = localStorage.getItem(STORAGE_KEY);
                if (designsStr) {
                    const designs: Design[] = JSON.parse(designsStr);
                    const slimDesigns = designs.map(d => {
                        if (d.id === finalDesign.id) return d;
                        return { ...d, thumbnail: '' };
                    });
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(slimDesigns));
                }
            }
        }

        return finalDesign;
    },

    // Eliminar diseño
    deleteDesign: async (id: string): Promise<void> => {
        // Obtener accountId del usuario actual
        const accountId = localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID';

        try {
            await apiFetch(`/api/designs/${id}?accountId=${accountId}`, { method: 'DELETE' });
        } catch (error) {
            console.error('Error deleting from API:', error);
        }

        // Always delete from local too
        const designsStr = localStorage.getItem(STORAGE_KEY);
        if (designsStr) {
            const designs: Design[] = JSON.parse(designsStr);
            const filtered = designs.filter(d => d.id !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
        }
    },

    // Exportar diseño como archivo .db
    exportDesignToFile: async (designId: string): Promise<void> => {
        // Try to get from API first to ensure we have latest
        let design = await DesignStorageService.getDesignById(designId);

        if (!design) return;

        const dataStr = JSON.stringify(design, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${design.name.replace(/\s+/g, '_')}.db`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },

    // Importar diseño desde archivo
    importDesignFromFile: (file: File): Promise<Design> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const content = e.target?.result as string;
                    const design = JSON.parse(content);

                    // Generar nuevo ID para evitar colisiones al importar
                    design.id = uuidv4();
                    design.importedAt = new Date().toISOString();

                    const saved = await DesignStorageService.saveDesign(design);
                    resolve(saved);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    }
};
